import { Link, makeStyles, Paper, Typography } from "@material-ui/core";
import React from "react";

import Icons8Edit from "../../../components/icons/svg/windows-10/Icons8Edit";
import { WebTrackerResolutionType } from "../../types";
import { resolutionTypeImperative } from "../TrackerResolution";
import { CannyRedirect } from "../../../destinations/modals/Canny";

const useStyles = makeStyles((theme) => ({
  docs: {
    backgroundColor: theme.palette.grey[50],
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 2),
  },
  docsLine: {
    marginBottom: theme.spacing(1),
  },
}));

type ReplaceWithFreshpaintExplanationProps = {
  hasFreshpaintIntegration: boolean;
  trackerName: string;
};

function ReplaceWithFreshpaintExplanation({
  hasFreshpaintIntegration,
  trackerName,
}: ReplaceWithFreshpaintExplanationProps) {
  const classes = useStyles();

  if (!hasFreshpaintIntegration) {
    return (
      <>
        <Typography className={classes.docsLine} component="p" variant="body1">
          {`Freshpaint does not currently have a HIPAA-compliant integration with ${trackerName}. `}
          <Link onClick={() => CannyRedirect()}>
            Let us know if you would like us to build it!
          </Link>
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography className={classes.docsLine} component="p" variant="body1">
        Freshpaint integrates with advertising platforms, analytics tools and
        other services. If you replace the third party tracking technology with
        Freshpaint, you can continue using your tools while maintaining HIPAA
        compliance.
      </Typography>
    </>
  );
}

export type TrackerResolutionExplanationProps = {
  webTrackerResolutionType: WebTrackerResolutionType;
  hasFreshpaintIntegration: boolean;
  trackerName: string;
};

function TrackerResolutionExplanation({
  webTrackerResolutionType,
  hasFreshpaintIntegration,
  trackerName,
}: TrackerResolutionExplanationProps) {
  const classes = useStyles();

  return (
    <Paper className={classes.docs} elevation={2}>
      <Typography className={classes.docsLine} component="h5" variant="h5">
        <Icons8Edit /> {resolutionTypeImperative(webTrackerResolutionType)}
      </Typography>
      {webTrackerResolutionType === "signed_baa" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            The tracker has been signed under a Business Associate Agreement
            (BAA).
          </Typography>
          <Typography component="p" variant="body1">
            A BAA is a contract between a covered entity and a business
            associate that ensures that the business associate will follow
            HIPAA&apos;s regulations.
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "tracker_blocked" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            Block the tracker codelessly using the Freshpaint Web Tracker
            Blocker.
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "tracker_first_party" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            If your organization owns this tracker, then there is no risk of
            sharing PHI with a third party.
          </Typography>
          <Typography component="p" variant="body1">
            Select this option moves the tracker to a “low risk” status,
            allowing you to focus on any remaining high risk items.
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "marked_as_safe" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            Mark the tracker as safe and leave it on your site.
          </Typography>
          <Typography component="p" variant="body1">
            Selecting this option moves the tracker to a “low risk” status,
            allowing you to focus on any remaining high risk items.
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "marked_for_removal" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            Indicate that you intend to remove the tracker from your site.
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "unresolved" && (
        <>
          <Typography
            className={classes.docsLine}
            component="p"
            variant="body1"
          >
            Reset the resolution state of this tracker to "Unresolved".
          </Typography>
        </>
      )}
      {webTrackerResolutionType === "replace_with_freshpaint" && (
        <ReplaceWithFreshpaintExplanation
          hasFreshpaintIntegration={hasFreshpaintIntegration}
          trackerName={trackerName}
        />
      )}
    </Paper>
  );
}

export default TrackerResolutionExplanation;
